import { useEffect, useId, useState } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EnergyDemand, fetchEnergyDemand } from "../../PVPotentialAPI";

const nf = (v: number | null | undefined, digits = 0) =>
  v == null ? "—" : new Intl.NumberFormat("de-DE", { maximumFractionDigits: digits }).format(v);

/** Effizienzklassen nach GEG Anlage 10, mit den Farben der Heatmap-Ebene. */
const CLASSES: [string, string, number][] = [
  ["A+", "#1a9850", 30], ["A", "#66bd63", 50], ["B", "#a6d96a", 75], ["C", "#d9ef8b", 100], ["D", "#fee08b", 130],
  ["E", "#fdae61", 160], ["F", "#f46d43", 200], ["G", "#d73027", 250], ["H", "#a50026", Infinity],
];

const FUELS: [string, string][] = [
  ["Gas", "Gas"], ["Heizoel", "Heizöl"], ["Fernwaerme", "Fernwärme"], ["Solar_Geothermie_Waermepumpen", "Wärmepumpe"],
  ["Strom", "Strom (direkt)"], ["Holz_Holzpellets", "Holz, Pellets"], ["Biomasse_Biogas", "Biomasse, Biogas"], ["Kohle", "Kohle"],
];

/**
 * Ersteinschätzung aus dem 3D-Modell (LoD2), dem Zensus und dem DWD — für Gebäude ohne Zähler. Baujahr, Energieträger
 * und Sanierungsstand lassen sich überschreiben, wenn sie bekannt sind.
 */
export default function EnergyDemandEstimate({ building }: { building: any }) {
  const id = useId();
  const buildingId = building?._id ? String(building._id) : building?.id;
  const [demand, setDemand] = useState<EnergyDemand | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState<string>("");
  const [fuel, setFuel] = useState<string>("");
  const [renovation, setRenovation] = useState<"typisch" | "unsaniert">("typisch");

  useEffect(() => {
    setDemand(null);
    setError(null);
    setYear("");
    setFuel("");
    setRenovation("typisch");
  }, [buildingId]);

  useEffect(() => {
    if (!buildingId) return;
    let cancelled = false;
    const y = Number(year);
    const timer = setTimeout(() => {
      fetchEnergyDemand(buildingId, { year: Number.isFinite(y) && y > 1500 ? y : undefined, fuel: fuel || undefined, renovation })
        .then(r => { if (!cancelled) { setDemand(r); setError(null); } })
        .catch(e => { if (!cancelled) setError(e?.message ?? String(e)); });
    }, year ? 400 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [buildingId, year, fuel, renovation]);

  if (error && !demand) return <p className="text-sm text-destructive">Energiebedarf konnte nicht geschätzt werden: {error}</p>;
  if (!demand) return <p className="text-sm text-muted-foreground">Schätze Energiebedarf …</p>;

  const fuelLabel = FUELS.find(([key]) => key === demand.fuel)?.[1] ?? demand.fuel;

  return <div className="flex flex-col gap-3 rounded-md border p-4">
    <h3 className="text-lg font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]">Energiebedarf (Ersteinschätzung)</h3>
    {error && <p className="text-sm text-destructive">Energiebedarf konnte nicht geschätzt werden: {error}</p>}
    {!demand.available ? <p className="text-sm text-muted-foreground">{demand.reason}.</p> : <>
      <div className="flex flex-wrap items-end gap-6">
        <div className="flex flex-col">
          <div className="text-xs text-muted-foreground">Effizienzklasse</div>
          <div className="flex gap-0.5" aria-label={`Effizienzklasse ${demand.efficiencyClass}`}>
            {CLASSES.map(([label, color]) => <div key={label}
              className={`flex h-8 w-8 items-center justify-center text-xs font-bold ${label === demand.efficiencyClass ? "ring-2 ring-[color:var(--customer-primary-dark,hsl(var(--foreground)))] ring-offset-1" : "opacity-40"}`}
              style={{ backgroundColor: color, color: ["A+", "A", "G", "H"].includes(label) ? "#fff" : "#222" }}>{label}</div>)}
          </div>
        </div>
        {[
          ["Endenergie", `${nf(demand.finalEnergyPerM2)} kWh/m²·a`],
          ["Im Jahr", `${nf(demand.finalEnergyKWh)} kWh`],
          ["CO₂", `${nf((demand.co2Kg ?? 0) / 1000, 1)} t/a`],
          ["Nutzfläche", `${nf(demand.usableAreaM2)} m²`],
        ].map(([label, text]) => <div key={label} className="flex flex-col">
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-xl font-bold tabular-nums">{text}</div>
        </div>)}
      </div>
      <p className="text-sm">
        Baujahr <span className="font-bold">{demand.year}</span> ({demand.yearSource}), {fuelLabel} ({demand.fuelSource}),
        {" "}{nf((demand.sharedWallShare ?? 0) * 100)} % der Außenwand am Nachbarn ({demand.sharedWallSource}).
        {demand.renovation === "typisch" && demand.unrenovated
          ? <> Unsaniert wären es {nf(demand.unrenovated.finalEnergyPerM2)} kWh/m², Klasse {demand.unrenovated.efficiencyClass}.</>
          : null}
      </p>
      <div className="grid min-w-0 gap-4 sm:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={`${id}-year`}>Baujahr, wenn bekannt</Label>
          <Input id={`${id}-year`} type="number" min={1500} max={2100} placeholder={String(demand.year ?? "")} value={year} onChange={e => setYear(e.target.value)} />
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={`${id}-fuel`}>Energieträger</Label>
          <Select value={fuel || "auto"} onValueChange={v => setFuel(v === "auto" ? "" : v)}>
            <SelectTrigger id={`${id}-fuel`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">wie geschätzt</SelectItem>
              {FUELS.map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={`${id}-renovation`}>Sanierungsstand</Label>
          <Select value={renovation} onValueChange={v => setRenovation(v as "typisch" | "unsaniert")}>
            <SelectTrigger id={`${id}-renovation`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="typisch">typisch (Fenster, Dach erneuert)</SelectItem>
              <SelectItem value="unsaniert">unsaniert</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        <p>{demand.caveat}</p>
        {demand.sources?.map(s => <p key={s}>{s}</p>)}
      </div>
    </>}
  </div>;
}
